import axios from 'axios'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Log requests in development
    if (import.meta.env.DEV) {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`)
    }

    // Check for impersonation state and inject token/header
    try {
      const impersonationData = sessionStorage.getItem('mindmetal-impersonation')
      if (impersonationData) {
        const parsed = JSON.parse(impersonationData)
        const state = parsed?.state

        if (state?.isImpersonating && state?.impersonationToken) {
          // Use the impersonation token instead of the regular one
          config.headers['Authorization'] = `Bearer ${state.impersonationToken}`
          // Also add header for backend to know about impersonation
          config.headers['X-Impersonation-Tenant-Id'] = state.tenantId
        }
      }
    } catch (e) {
      // Ignore parsing errors, proceed without impersonation
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Skip token refresh for auth endpoints - they handle 401s directly
    const isAuthEndpoint = originalRequest.url?.startsWith('/auth/')

    // Handle 401 - try to refresh token (but not for auth endpoints)
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true

      try {
        // Import here to avoid circular dependency
        const { useAuthStore } = await import('../hooks/useAuth')
        await useAuthStore.getState().refresh()

        // Retry original request
        return api(originalRequest)
      } catch (refreshError) {
        // Refresh failed, logout
        const { useAuthStore } = await import('../hooks/useAuth')
        useAuthStore.getState().logout()
        window.location.href = '/auth/signin'
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

export default api
